const SUMMARY_CHARACTER_LIMIT = 140;
const RELEASE_TYPE_CLASS_MAP = {
  major: 'badge-major',
  minor: 'badge-minor',
  patch: 'badge-patch',
};

const buildInitialWhatsNewModalState = (show = false) => ({
  localShow: show,
  activeReleaseIndex: 0,
});

const getActiveWhatsNewRelease = ({
  activeReleaseIndex = 0,
  releases = [],
} = {}) =>
  (Array.isArray(releases) ? releases[activeReleaseIndex] : null) || null;

const shouldUseWhatsNewSummary = (
  translatedHighlights = [],
  summaryLimit = SUMMARY_CHARACTER_LIMIT
) =>
  Array.isArray(translatedHighlights) &&
  translatedHighlights.length > 0 &&
  String(translatedHighlights[0] || '').length > summaryLimit;

const getWhatsNewReleaseSummary = ({
  summaryLimit = SUMMARY_CHARACTER_LIMIT,
  translatedHighlights = [],
} = {}) =>
  shouldUseWhatsNewSummary(translatedHighlights, summaryLimit)
    ? translatedHighlights[0]
    : '';

const parseWhatsNewHighlight = (highlight = '') => {
  const text = String(highlight || '');
  const separators = [' — ', ': '];
  const separator = separators.find((value) => text.includes(value));

  if (!separator) {
    return {
      title: text.split(' ').slice(0, 5).join(' '),
      description: text,
    };
  }

  const [title, ...descriptionParts] = text.split(separator);
  return {
    title: title.trim(),
    description: descriptionParts.join(separator).trim(),
  };
};

const buildStructuredWhatsNewHighlights = ({
  parseHighlight = parseWhatsNewHighlight,
  summaryLimit = SUMMARY_CHARACTER_LIMIT,
  translatedHighlights = [],
} = {}) => {
  const highlights = Array.isArray(translatedHighlights)
    ? translatedHighlights
    : [];
  const startIndex = shouldUseWhatsNewSummary(highlights, summaryLimit) ? 1 : 0;

  return highlights.slice(startIndex).map((highlight, index) => {
    const split = parseHighlight(highlight);
    return {
      originalIndex: index + startIndex,
      title: split.title,
      description: split.description,
    };
  });
};

const formatWhatsNewReleaseDate = ({ dateString = '', locale = 'it' } = {}) => {
  if (!dateString) {
    return '';
  }

  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const resolveWhatsNewReleaseTypeClass = (type = '') =>
  RELEASE_TYPE_CLASS_MAP[type] || RELEASE_TYPE_CLASS_MAP.patch;

const resolveWhatsNewTranslatedTitle = ({
  release = {},
  translate = (key) => key,
} = {}) => {
  if (release?.titleKey) {
    const translated = translate(release.titleKey);
    if (translated !== release.titleKey) {
      return translated;
    }
  }

  return release?.title;
};

const resolveWhatsNewTranslatedHighlights = ({
  release = {},
  translate = (key) => key,
} = {}) => {
  const highlightKeys = Array.isArray(release?.highlightKeys)
    ? release.highlightKeys
    : [];

  if (highlightKeys.length > 0) {
    return highlightKeys.map((key) => {
      const translated = translate(key);
      return translated !== key ? translated : key;
    });
  }

  return Array.isArray(release?.highlights) ? release.highlights : [];
};

const getWhatsNewImageForHighlight = ({
  highlightIndex = 0,
  release = {},
} = {}) => {
  const images = Array.isArray(release?.images) ? release.images : [];
  if (images.length === 0) {
    return null;
  }

  return (
    images.find((image) => image.highlightIndex === highlightIndex) || null
  );
};

export {
  SUMMARY_CHARACTER_LIMIT,
  buildInitialWhatsNewModalState,
  buildStructuredWhatsNewHighlights,
  formatWhatsNewReleaseDate,
  getActiveWhatsNewRelease,
  getWhatsNewImageForHighlight,
  getWhatsNewReleaseSummary,
  parseWhatsNewHighlight,
  resolveWhatsNewReleaseTypeClass,
  resolveWhatsNewTranslatedHighlights,
  resolveWhatsNewTranslatedTitle,
  shouldUseWhatsNewSummary,
};
